import styled, { keyframes } from 'styled-components';

export const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

export const Container = styled.div`
  padding: 30px;
  background: #f8f9fa;
  min-height: 100vh;
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
  animation: ${fadeIn} 0.5s ease-out;
`;

export const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
`;

export const CreateButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(0, 123, 255, 0.4);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 123, 255, 0.6);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const FilterBar = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

export const StatusTagGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const StatusTag = styled.button<{ active?: boolean; statusType?: 'all' | 'draft' | 'published' }>`
  padding: 8px 14px;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  ${(p) => {
    const palette = (() => {
      if (p.statusType === 'draft') {
        return {
          strongBg: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
          softBg: 'rgba(220, 53, 69, 0.12)',
          text: '#8b1a25',
          shadow: 'rgba(220, 53, 69, 0.25)',
        };
      }
      if (p.statusType === 'published') {
        return {
          strongBg: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          softBg: 'rgba(17, 153, 142, 0.14)',
          text: '#0f5132',
          shadow: 'rgba(56, 239, 125, 0.25)',
        };
      }
      return {
        strongBg: 'linear-gradient(135deg, #64748b 0%, #334155 100%)',
        softBg: '#e2e8f0',
        text: '#1f2937',
        shadow: 'rgba(100, 116, 139, 0.25)',
      };
    })();

    const background = p.active ? palette.strongBg : palette.softBg;
    const color = p.active ? '#ffffff' : palette.text;
    const shadow = p.active ? `0 6px 14px ${palette.shadow}` : '0 2px 6px rgba(15, 23, 42, 0.08)';
    const border = p.active ? 'none' : `1px solid ${palette.softBg}`;

    return `
      color: ${color};
      background: ${background};
      box-shadow: ${shadow};
      border: ${border};
    `;
  }}
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  min-width: 250px;
  padding: 12px 20px;
  border: 2px solid #e1e8ed;
  border-radius: 12px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  &::placeholder {
    color: #aab8c2;
  }
`;

export const FilterSelect = styled.select`
  padding: 12px 20px;
  border: 2px solid #e1e8ed;
  border-radius: 12px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

export const CardsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: ${fadeIn} 0.7s ease-out;
`;

export const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

export const RowCommon = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

export const InfoColumn = styled.div`
  flex: 1 1 380px;
  min-width: 280px;
`;

export const MetaColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  min-width: 260px;

  @media (max-width: 900px) {
    align-items: flex-start;
  }
`;

export const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

export const RowPublished = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 8px 0 2px;
`;

export const ExamCard = styled.div<{ status: string }>`
  background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
  border-radius: 16px;
  padding: 18px 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(0, 123, 255, 0.1);
  display: flex;
  align-items: flex-start;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.status === 'published' 
      ? 'linear-gradient(90deg, #11998e 0%, #38ef7d 100%)' 
      : 'linear-gradient(90deg, #dc3545 0%, #c82333 100%)'
    };
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 123, 255, 0.15);
    background: linear-gradient(135deg, #ffffff 0%, #e7f3ff 100%);
  }
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  gap: 12px;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const CardHeaderContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ExamTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const ClassInfo = styled.div`
  font-size: 12px;
  color: #657786;
  margin-top: 4px;
`;

export const StatusBadge = styled.span<{ status: string }>`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.status === 'published' 
    ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' 
    : 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)'
  };
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const ExamDescription = styled.p`
  color: #657786;
  font-size: 14px;
  line-height: 1.6;
  margin: 4px 0 6px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const ExamMeta = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  flex-wrap: wrap;
`;

export const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #657786;
  font-size: 13px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const MetaLabel = styled.span`
  font-weight: 600;
  color: #2c3e50;
`;

export const DifficultyBadge = styled.span<{ level: string }>`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => {
    switch (props.level) {
      case 'easy': return '#d4edda';
      case 'medium': return '#fff3cd';
      case 'hard': return '#f8d7da';
      default: return '#e2e3e5';
    }
  }};
  color: ${props => {
    switch (props.level) {
      case 'easy': return '#155724';
      case 'medium': return '#856404';
      case 'hard': return '#721c24';
      default: return '#383d41';
    }
  }};
`;

export const DifficultyContainer = styled.div`
  margin: 0;
`;

export const ShareLinkContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 0;
  background: transparent;
  width: fit-content;
`;

export const ShareLinkLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #0369a1;
  white-space: nowrap;
`;

export const CopyButton = styled.button`
  padding: 7px 16px;
  background: linear-gradient(135deg, #5b8df3 0%, #4169e1 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-shadow: 0 6px 16px rgba(65, 105, 225, 0.25);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(65, 105, 225, 0.35);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const CardActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

export const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  flex: 0 0 auto;
  min-width: 96px;
  padding: 8px 12px;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => {
    switch (props.variant) {
      case 'primary': return 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)';
      case 'danger': return 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)';
      default: return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    }
  }};
  color: white;
  box-shadow: ${props => props.variant === 'secondary'
    ? '0 6px 16px rgba(118, 75, 162, 0.25)'
    : '0 6px 16px rgba(0, 123, 255, 0.25)'};

  &:hover {
    transform: translateY(-2px);
    opacity: 0.95;
  }

  &:active {
    transform: translateY(0);
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #657786;

  svg {
    width: 120px;
    height: 120px;
    margin-bottom: 20px;
    opacity: 0.3;
  }

  h3 {
    font-size: 24px;
    color: #2c3e50;
    margin-bottom: 12px;
  }

  p {
    font-size: 16px;
    margin-bottom: 24px;
  }
`;

export const LoadingCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 18px 20px;
  height: 140px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 2000px 100%;
  animation: ${shimmer} 2s infinite;
`;

export const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 40px;
  animation: ${fadeIn} 0.8s ease-out;
`;

export const PageButton = styled.button<{ active?: boolean }>`
  padding: 10px 16px;
  border: 2px solid ${props => props.active ? '#007bff' : '#e1e8ed'};
  background: ${props => props.active ? '#007bff' : 'white'};
  color: ${props => props.active ? 'white' : '#2c3e50'};
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? '#007bff' : '#f5f7fa'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
